import { useCallback, useEffect } from "react";

import PropTypes from "prop-types";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { InputAdornment, Paper, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";

import { useOutletApi } from "@/features/authentication";
import { formatDate, formatMoney, getErrorMessage } from "@/features/shared";
import {
  CustomAsyncAutocomplete,
  CustomNumberField,
  useSnackbar,
} from "@/features/ui";

export const SelectSalesOrderSection = ({ editMode = false }) => {
  const { control, setValue } = useFormContext();
  const { showSnackbar } = useSnackbar();

  const [buyer, seller, salesOrder] = useWatch({
    control,
    name: ["buyer", "seller", "salesOrder"],
    exact: true,
  });

  const outletApi = useOutletApi(seller?.id);

  const disableBrowseSalesOrder = !buyer || !seller;

  // Use primitive ids for downstream deps so we don't react to
  // object-identity churn from useWatch when unrelated form fields change.
  const buyerId = buyer?.id;
  const sellerId = seller?.id;
  const refetchKey = `${buyerId ?? ""}-${sellerId ?? ""}`;

  // Clear stale SO + amount when buyer or seller changes so the user
  // can't submit a salesOrder that no longer matches the buyer/seller pair.
  // In edit mode buyer/seller/SO are locked, so skip the reset to preserve
  // the prefilled salesOrder and amount.
  useEffect(() => {
    if (editMode) return;
    setValue("salesOrder", null);
    setValue("amount", null);
  }, [buyerId, sellerId, editMode, setValue]);

  const fetchSalesOrders = useCallback(
    async (searchValue, { page, size } = {}) => {
      if (!buyer || !seller) return [];

      const {
        data: { data },
      } = await outletApi.get("sales-orders", {
        params: {
          page,
          size,
          buyerableId: buyer.id,
          referenceNumber: searchValue || null,
        },
      });
      return data;
    },
    [buyer, seller, outletApi],
  );

  const handleSalesOrderChange = useCallback(
    async (option, onChange) => {
      setValue("amount", null);

      if (!option) {
        onChange(null);
        return;
      }

      try {
        const {
          data: { data },
        } = await outletApi.get(`sales-orders/${option.referenceNumber}`, {
          params: { withAvailableDownPaymentAmount: true },
        });
        onChange(data);
      } catch (error) {
        showSnackbar(
          `Gagal memuat detail pesanan. ${getErrorMessage(error)}`,
        );
        onChange(option);
      }
    },
    [setValue, showSnackbar, outletApi],
  );

  return (
    <Paper sx={{ p: 2.5, borderRadius: 2 }}>
      <Stack spacing={2.5}>
        <Typography variant="h6">Pesanan Penjualan</Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              control={control}
              name="salesOrder"
              render={({ field, fieldState }) => (
                <CustomAsyncAutocomplete
                  value={field.value}
                  onChange={(value) =>
                    handleSalesOrderChange(value, field.onChange)
                  }
                  disabled={editMode || disableBrowseSalesOrder}
                  fetchOptions={fetchSalesOrders}
                  refetchKey={refetchKey}
                  getOptionLabel={(option) => option.referenceNumber}
                  isOptionEqualToValue={(option, selectedOption) =>
                    option.id === selectedOption.id
                  }
                  renderOption={(props, option) => {
                    const { id, referenceNumber, transactionDate } = option;
                    return (
                      <li {...props} key={id}>
                        <div>
                          <div>{referenceNumber}</div>
                          <div style={{ fontSize: "0.75rem" }}>
                            {formatDate(transactionDate)}
                          </div>
                        </div>
                      </li>
                    );
                  }}
                  TextFieldProps={{
                    required: true,
                    label: "Pesanan Penjualan",
                    error: fieldState.invalid,
                    helperText:
                      fieldState.error?.message ||
                      (disableBrowseSalesOrder
                        ? "Pilih Pelanggan dan Penjual terlebih dahulu"
                        : null),
                    InputProps: {
                      placeholder: "Cari nomor referensi pesanan",
                    },
                  }}
                />
              )}
            />
          </Grid>
          {salesOrder && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="amount"
                render={({ field, fieldState }) => (
                  <CustomNumberField
                    value={field.value ?? ""}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                    required
                    label="Uang Muka"
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">Rp</InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          )}
          {salesOrder && (
            <>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  Tanggal Pesanan
                </Typography>
                <Typography variant="body1">
                  {formatDate(salesOrder.transactionDate)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  Grand Total Pesanan
                </Typography>
                <Typography variant="body1">
                  {formatMoney(salesOrder.grandTotal)}
                </Typography>
              </Grid>
              {salesOrder.availableDownPaymentAmount != null && (
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="body2" sx={{
                    color: "text.secondary"
                  }}>
                    Sisa Uang Muka Tersedia
                  </Typography>
                  <Typography variant="body1" sx={{
                    fontWeight: "bold"
                  }}>
                    {formatMoney(salesOrder.availableDownPaymentAmount)}
                  </Typography>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Stack>
    </Paper>
  );
};

SelectSalesOrderSection.propTypes = {
  editMode: PropTypes.bool,
};
